import React, { useState, useEffect } from 'react';
import { Cookie, X, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

export default function CookieConsent() {
  const { t } = useLanguage();
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, can't be disabled
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      // Show banner after 1 second
      setTimeout(() => setShowBanner(true), 1000);
    }

    // Listen for "Gérer les cookies" button from footer
    const handleOpenSettings = () => {
      setShowBanner(true);
      setShowSettings(true);
    };
    window.addEventListener('openCookieSettings', handleOpenSettings);
    return () => window.removeEventListener('openCookieSettings', handleOpenSettings);
  }, []);

  const savePreferences = (prefs) => {
    const consent = {
      necessary: true,
      analytics: prefs.analytics,
      marketing: prefs.marketing,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('cookie_consent', JSON.stringify(consent));
    
    // Here you would typically initialize/disable analytics and marketing scripts
    if (prefs.analytics) {
      // Initialize Google Analytics
    }
    if (prefs.marketing) {
      // Initialize marketing pixels
    }
    
    setShowBanner(false);
    setShowSettings(false);
  };

  const acceptAll = () => {
    savePreferences({ analytics: true, marketing: true });
  };

  const rejectAll = () => {
    savePreferences({ analytics: false, marketing: false });
  };

  const saveCustom = () => {
    savePreferences(preferences);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-4 left-4 z-50 max-w-md animate-in slide-in-from-bottom-4 duration-500">
      {/* Main Banner */}
      {!showSettings ? (
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
          <div className="p-5">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-[#00aaff]/10 flex items-center justify-center shrink-0">
                <Cookie size={20} className="text-[#00aaff]" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 text-base mb-1">{t('cookie.title')}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {t('cookie.description')}
                </p>
              </div>
              <button
                onClick={() => setShowBanner(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors shrink-0"
                aria-label="Fermer"
              >
                <X size={18} />
              </button>
            </div>

            <div className="flex flex-col gap-2">
              <button
                onClick={acceptAll}
                className="w-full bg-[#00aaff] hover:bg-[#0099ee] text-white font-semibold py-2.5 px-4 rounded-lg transition-colors text-sm"
              >
                {t('cookie.acceptAll')}
              </button>
              <div className="flex gap-2">
                <button
                  onClick={rejectAll}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2.5 px-4 rounded-lg transition-colors text-sm"
                >
                  {t('cookie.reject')}
                </button>
                <button
                  onClick={() => setShowSettings(true)}
                  className="flex-1 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 font-medium py-2.5 px-4 rounded-lg transition-colors text-sm flex items-center justify-center gap-2"
                >
                  <Settings size={16} />
                  {t('cookie.customize')}
                </button>
              </div>
              <Link
                to="/politique-cookies"
                className="text-xs text-gray-500 hover:text-[#00aaff] text-center transition-colors mt-1"
              >
                {t('cookie.learnMore')}
              </Link>
            </div>
          </div>
        </div>
      ) : (
        /* Settings Panel */
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
          <div className="p-5">
            <div className="flex items-start justify-between mb-4">
              <h3 className="font-bold text-gray-900 text-base">Paramètres des cookies</h3>
              <button
                onClick={() => setShowSettings(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Retour"
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-4 mb-5">
              {/* Necessary */}
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <p className="font-semibold text-gray-900 text-sm mb-0.5">Cookies nécessaires</p>
                  <p className="text-xs text-gray-500">Essentiels au fonctionnement du site</p>
                </div>
                <div className="relative inline-block w-11 h-6 shrink-0">
                  <input
                    type="checkbox"
                    checked={true}
                    disabled
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer-checked:bg-[#00aaff] opacity-50 cursor-not-allowed"></div>
                  <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform peer-checked:translate-x-5"></div>
                </div>
              </div>

              {/* Analytics */}
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <p className="font-semibold text-gray-900 text-sm mb-0.5">Cookies analytiques</p>
                  <p className="text-xs text-gray-500">Analyse du trafic et des performances</p>
                </div>
                <div className="relative inline-block w-11 h-6 shrink-0">
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer-checked:bg-[#00aaff] cursor-pointer" onClick={() => setPreferences({ ...preferences, analytics: !preferences.analytics })}></div>
                  <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform peer-checked:translate-x-5 cursor-pointer" onClick={() => setPreferences({ ...preferences, analytics: !preferences.analytics })}></div>
                </div>
              </div>

              {/* Marketing */}
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <p className="font-semibold text-gray-900 text-sm mb-0.5">Cookies marketing</p>
                  <p className="text-xs text-gray-500">Publicité personnalisée</p>
                </div>
                <div className="relative inline-block w-11 h-6 shrink-0">
                  <input
                    type="checkbox"
                    checked={preferences.marketing}
                    onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer-checked:bg-[#00aaff] cursor-pointer" onClick={() => setPreferences({ ...preferences, marketing: !preferences.marketing })}></div>
                  <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform peer-checked:translate-x-5 cursor-pointer" onClick={() => setPreferences({ ...preferences, marketing: !preferences.marketing })}></div>
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={saveCustom}
                className="flex-1 bg-[#00aaff] hover:bg-[#0099ee] text-white font-semibold py-2.5 px-4 rounded-lg transition-colors text-sm"
              >
                Enregistrer
              </button>
              <button
                onClick={acceptAll}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2.5 px-4 rounded-lg transition-colors text-sm"
              >
                Tout accepter
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
